import React, { Fragment } from 'react'
import p from 'prop-types'
import styles from './Form.scss'
import cx from 'classnames'
import _ from 'lodash'
import Textarea from 'react-textarea-autosize'
import Select from 'react-select'
import { CTA } from 'common/styleguide/CTA'
import { getPhotoUploadMaxConcurrency } from 'lib/utils'
import PromisePool from 'es6-promise-pool'
import { DroppablePage } from 'common/DroppablePage'
import { CircleSpinner } from 'common/spinners'
import { photoGear } from 'lib/urlHelpers'
import fetchJSON from 'lib/fetchJSON'
import { Icon } from 'common/Icon'

export class Form extends React.PureComponent {
  static propTypes = {
    onSave: p.func.isRequired,
    onCancel: p.func.isRequired,
    onLocationSearch: p.func.isRequired,
    poi: p.object.isRequired,
    tags: p.array.isRequired,
    errors: p.object
  }

  constructor (props) {
    super(props)
    const { poi, routes } = props
    this.state = {
      searchLocation: '',
      name: poi.name,
      description: poi.description,
      url: poi.url,
      tagNames: poi.tagNames,
      photoIds: poi.photoIds,
      routesSelected: _.fromPairs(routes.map(r => ([r.id, true]))),
      pendingUploads: 0
    }
  }

  onDrop = (files) => {
    const { poi } = this.props
    this.setState((prevState) => ({ pendingUploads: prevState.pendingUploads + files.length }))
    let index = 0
    const nextUpload = () => {
      if (!files[index]) { return null }
      const nextUpload = files[index]
      index++
      return this.addPhoto(poi.id, nextUpload)
        .then(() => this.setState((prevState) => ({ pendingUploads: prevState.pendingUploads - 1 })))
    }
    const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())

    pool.start()
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  onNameChange = ({ target: { value: name } }) => {
    this.setState({ name })
  }

  onDescriptionChange = ({ target: { value: description } }) => {
    this.setState({ description })
  }

  onUrlChange = ({ target: { value: url } }) => {
    this.setState({ url })
  }

  onSearchLocationChange = ({ target: { value: searchLocation } }) => {
    this.setState({ searchLocation })
  }

  onSearchLocationKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.props.onLocationSearch(this.state.searchLocation)
    }
  }

  onTagNamesChange = (values) => {
    const tagNames = values.map(v => v.value)
    this.setState({ tagNames })
  }

  onSelectedRouteChange = (e) => {
    this.setState({
      routesSelected: { ...this.state.routesSelected, [+e.target.value]: e.target.checked }
    })
  }

  poiState () {
    const { name, description, url, tagNames } = this.state
    return { name, description, url, tagNames }
  }

  onSubmit = (event) => {
    const { routesSelected } = this.state
    event.preventDefault()
    this.props.onSave({ poi: this.poiState(), routeIds: Object.keys(routesSelected).filter(id => routesSelected[id]) })
  }

  addPhoto = (poiId, photo) => {
    const body = new window.FormData()
    body.append('file', photo)
    body.append('parent_type', 'point_of_interest')
    body.append('parent_id', poiId)
    return fetchJSON('/photos.json', null, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(({ photo }) => {
      this.setState({ photoIds: this.state.photoIds.concat([photo.id]) })
    })
  }

  removePhoto (photoId) {
    fetchJSON(`/photos/${photoId}.json`, null, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(() => {
      this.setState({ photoIds: this.state.photoIds.filter(id => id !== photoId) })
    })
  }

  render () {
    const { routes, tags, onCancel } = this.props
    const { routesSelected, name, description, url, tagNames, photoIds, pendingUploads } = this.state

    const tagValues = tags.map(t => ({ value: t, label: t }))
    const poiTagNames = tagNames.map(t => ({ value: t, label: t }))

    const errors = (this.props.errors && this.props.errors.toObject()) || {}
    if (errors.lat || errors.lng) {
      errors.base || (errors.base = [])
      errors.base.push('You must choose a location for the POI')
    }

    return (
      <div className={styles.formContainer}>
        <DroppablePage
          ref={r => { this._dropzone = r }}
          multiple
          onDrop={this.onDrop}>
          <form className={styles.form} onSubmit={this.onSubmit}>
            <div className={styles.formContents}>
              {errors.base && <div className={styles.formErrors}>{errors.base}</div>}
              <div className={styles.formGroup}>
                <label>Jump to a Location</label>
                <div className={styles.locationInput}>
                  <input onChange={this.onSearchLocationChange} onKeyPress={this.onSearchLocationKeyPress} />
                  <span className='icon icon-search' />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Name *
                  {errors.name && <span className={styles.errorMsg}>{errors.name}</span>}
                </label>
                <div className={cx(styles.name, errors.name && styles.errorHighlight)}>
                  <input defaultValue={name} onChange={this.onNameChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Tags</label>
                <div className={styles.tagsInput}>
                  <Select.Creatable
                    multi
                    joinValues
                    delimiter=','
                    value={poiTagNames}
                    onChange={this.onTagNamesChange}
                    options={tagValues}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                {errors.url && <div className={styles.errorMsg}>{errors.url}</div>}
                <label>URL</label>
                <div className={styles.urlInput}>
                  <input defaultValue={url} onChange={this.onUrlChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <div className={styles.descriptionInput}>
                  <Textarea defaultValue={description} onChange={this.onDescriptionChange} />
                </div>
              </div>
              <div className={styles.photos}>
                {photoIds.length === 0 && !pendingUploads && <Fragment>
                  <h2>Photos</h2>
                  <div>Drag photos onto this page or <a onClick={this.onTriggerFileBrowser}>click here</a> to add photos to this route.</div>
                </Fragment>}
                {pendingUploads > 0 && <p><CircleSpinner size={16} /> Uploading {pendingUploads} photo{pendingUploads > 1 ? 's' : ''}...</p>}
                <div className={styles.photoGrid}>
                  {photoIds.map(id => (
                    <div className={styles.photo} key={id}>
                      <div className={styles.removePhoto} onClick={() => this.removePhoto(id)}>
                        <Icon size={20} i='ios-close' />
                      </div>
                      <img src={photoGear(id)} />
                    </div>
                  ))}
                </div>
                {photoIds.length > 0 && <div className={styles.morePhotos}>
                  <a onClick={this.onTriggerFileBrowser} data-tip title='You can also add photos by dragging them onto this page'>Add photos</a>
                </div>}
              </div>
              {routes && routes.length > 0 && <div className={styles.formGroup}>
                <label>Routes</label>
                {routes.map(r => (
                  <label key={r.id} className={styles.checkboxLabel}>
                    <input type='checkbox' checked={routesSelected[r.id]} onChange={this.onSelectedRouteChange} value={r.id} />
                    <a href={`/routes/${r.id}`} target='showRoute'>{r.name}</a>
                  </label>
                ))}
              </div>}
            </div>
            <div className={styles.actions}>
              <CTA link onClick={onCancel} margins={false}>
                Cancel
              </CTA>
              <CTA button type='submit' margins={false}>
                Save
              </CTA>
            </div>
          </form>
        </DroppablePage>
      </div>
    )
  }
}
